import type { CallExpressionShape, Diagnostics, ObjectShape, Shape } from '@servicenow/sdk-build-core'

/**
 * Detects duplicate `order` values across a sibling collection (lanes + stage-level
 * decisions in a playbook, or activities + lane-level decisions inside a lane) and
 * emits a diagnostic for each collision.
 *
 * Anchors the error on the second occurrence's `order` shape so the error renders
 * at the offending source location.
 */
export function validateOrderUniqueness(
    children: Array<{ callExpr: CallExpressionShape; childConfig: ObjectShape }>,
    diagnostics: Diagnostics
): void {
    const seen = new Map<number, { label: string; shape: Shape }>()

    for (const { childConfig } of children) {
        const orderShape = childConfig.get('order')
        const orderValue = orderShape?.ifNumber()?.getValue()
        if (orderValue === undefined) {
            continue
        }

        // `label` is required by LaneConfig and ActivityConfig; the cast trusts the type.
        const label = childConfig.get('label')?.ifString()?.getValue() as string
        const prior = seen.get(orderValue)
        if (prior) {
            diagnostics.error(
                orderShape ?? childConfig,
                `Duplicate order ${orderValue}: '${label}' and '${prior.label}' share order ${orderValue}`
            )
        } else {
            seen.set(orderValue, { label, shape: orderShape ?? childConfig })
        }
    }
}
